<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import { modalManagerToken } from "@/tokens";
    import InstagramPostModal from "@apps/instagram/components/InstagramPostModal/InstagramPostModal.svelte";
    import InstagramPostModalManager from "@apps/instagram/components/InstagramPostModal/InstagramPostModalManager";
    import InstagramStoryModal from "@apps/instagram/components/InstagramStoryModal/InstagramStoryModal.svelte";
    import InstagramStoryModalManager from "@apps/instagram/components/InstagramStoryModal/InstagramStoryModalManager";
    import {
        INSTAGRAM_GRID_LAYOUT,
        INSTAGRAM_LISTING_LAYOUT,
        INSTAGRAM_MASONRY_LAYOUT,
        INSTAGRAM_SLIDE_LAYOUT,
    } from "@apps/instagram/constants";
    import type InstagramCustomSettingsEntity from "@apps/instagram/entities/InstagramCustomSettingsEntity";
    import type InstagramDisplaySettingsEntity from "@apps/instagram/entities/InstagramDisplaySettingsEntity";
    import type InstagramGalleryEntity from "@apps/instagram/entities/InstagramGalleryEntity";
    import { instagramStoriesModalToken } from "@apps/instagram/tokens";
    import { hasNextPage } from "@apps/instagram/utils/paginationUtils";
    import { getTypedContext } from "@lib/svelte-typed-context";
    import { lastValueFrom, map } from "rxjs";
    import { onMount } from "svelte";
    import { container } from "tsyringe";

    import GrowaveBrand from "@components/GrowaveBrand/GrowaveBrand.svelte";
    import LoadMore from "@components/LoadMore.svelte";
    import { createModalToken } from "@components/Modal/ModalManager";
    import ModalManagerProvider from "@components/Modal/ModalManager/ModalManagerProvider.svelte";

    import { instagramGalleryWidgetContextToken } from "../context_tokens";
    import InstagramGalleryGridLayout from "../layouts/InstagramGalleryGridLayout.svelte";
    import InstagramGalleryListingLayout from "../layouts/InstagramGalleryListingLayout.svelte";
    import InstagramGalleryMasonryLayout from "../layouts/InstagramGalleryMasonryLayout.svelte";
    import InstagramGallerySlideLayout from "../layouts/InstagramGallerySlideLayout.svelte";
    import InstagramStoriesSlideList from "./InstagramStoriesSlideList.svelte";

    export let gallery: InstagramGalleryEntity;
    export let settings: InstagramDisplaySettingsEntity &
        InstagramCustomSettingsEntity;
    export let galleryLayout: number;
    export let isCurrentAppGrowave: boolean;

    const context = getTypedContext(instagramGalleryWidgetContextToken);

    const {
        taggedProductService,
        instagramPostsQuery,
        instagramPostsStore,
        instagramStoriesQuery,
        instagramStoriesStore,
        instagramStoriesService,
        taggedProductQuery,
        productsService,
        productsQuery,
        gwi18n,
    } = context;
    const modalManager = container.resolve(modalManagerToken);

    const instagramPostModalToken = createModalToken<{ id: number }>(
        `InstagramPostModal-${gallery.id}-${galleryLayout}`
    );

    const posts$ = instagramPostsQuery.all$;

    const postsStatus$ = instagramPostsQuery.status$;
    const postModalManager = new InstagramPostModalManager(
        instagramPostsQuery,
        instagramPostsStore,
        taggedProductService,
        productsService
    );
    const loadMoreT$ = gwi18n.selectT(
        "instagram.critical.InstagramGalleryWidget",
        "load_more"
    );
    const paginationMeta$ = instagramPostsQuery.paginationMeta$;
    const hasNextPage$ = paginationMeta$.pipe(
        map((paginationMeta) => {
            return hasNextPage(
                paginationMeta.perPage,
                paginationMeta.currentOffset,
                paginationMeta.totalCount
            );
        })
    );
    const stories$ = instagramStoriesQuery.all$;
    const storiesError$ = instagramStoriesQuery.error$;
    const storiesLoading$ = instagramStoriesQuery.loading$;
    const instagramStoryModalManager = new InstagramStoryModalManager(
        instagramStoriesQuery,
        instagramStoriesStore
    );
    onMount(async () => {
        if (
            gallery.storiesAccount &&
            settings.storiesEnabled &&
            !settings.forceDisabledStories
        )
            lastValueFrom(
                instagramStoriesService.getGalleryStories({
                    galleryId: gallery.id,
                    widgetId: galleryLayout,
                })
            );
    });
</script>

<ModalManagerProvider {modalManager}>
    {#if !$storiesError$ && settings.storiesEnabled && gallery.storiesAccount && !settings.forceDisabledStories}
        {#each [null] as _ (Math.random())}
            <InstagramStoriesSlideList
                skeleton={$storiesLoading$}
                {instagramStoriesModalToken}
                stories={$stories$}
            />
        {/each}
        <InstagramStoryModal
            {instagramStoriesModalToken}
            {instagramStoryModalManager}
        />
    {/if}

    <GrowaveBrand
        class="gw-instagram-gallery-brand"
        canShowWatermark={!!$posts$.length && gallery && !gallery.hideBranding}
    />

    {#if ![STATUS.VIRGIN, STATUS.ERROR].includes($postsStatus$) && gallery && settings}
        {#if settings.layout === INSTAGRAM_GRID_LAYOUT}
            <InstagramGalleryGridLayout
                skeleton={$postsStatus$ === STATUS.INITIAL_LOADING}
                skeletonPostsCount={settings.rows * settings.columns}
                postModalToken={instagramPostModalToken}
                columns={settings.columns}
                rows={settings.rows}
                gutter={settings.space}
                posts={$posts$}
                displayMode={settings.previewButtonTypeOnTaggedPost}
                highQualityEnabled={settings.highQualityEnabled}
            />
        {:else if settings.layout === INSTAGRAM_LISTING_LAYOUT && $posts$.length}
            <InstagramGalleryListingLayout
                skeleton={$postsStatus$ === STATUS.INITIAL_LOADING}
                skeletonPostsCount={settings.rows * settings.columns}
                postModalToken={instagramPostModalToken}
                columns={settings.columns}
                rows={settings.rows}
                posts={$posts$}
                displayMode={settings.previewButtonTypeOnTaggedPost}
                highQualityEnabled={settings.highQualityEnabled}
            />
        {:else if settings.layout === INSTAGRAM_SLIDE_LAYOUT}
            <InstagramGallerySlideLayout
                skeleton={$postsStatus$ === STATUS.INITIAL_LOADING}
                skeletonPostsCount={settings.visiblePostCountForSlider || 6}
                postModalToken={instagramPostModalToken}
                posts={$posts$}
                perPage={settings.visiblePostCountForSlider || 6}
                mobilePerPage={settings.visiblePostCountForSliderOnMobile || 3}
                gutter={settings.space}
                displayMode={settings.previewButtonTypeOnTaggedPost}
                loop={settings.sliderLoopEnabled}
                highQualityEnabled={settings.highQualityEnabled}
                autoPlayIntervalForSlider={settings.autoPlayIntervalForSlider}
                on:loadMore
            />
        {:else if settings.layout === INSTAGRAM_MASONRY_LAYOUT}
            <InstagramGalleryMasonryLayout
                skeleton={$postsStatus$ === STATUS.INITIAL_LOADING}
                postModalToken={instagramPostModalToken}
                gutter={settings.space}
                posts={$posts$}
                displayMode={settings.previewButtonTypeOnTaggedPost}
                highQualityEnabled={settings.highQualityEnabled}
            />
        {/if}
        {#if settings.layout !== INSTAGRAM_SLIDE_LAYOUT && $hasNextPage$ && settings.loadMoreEnabled}
            <LoadMore
                text={$loadMoreT$}
                ariaLabel="load more posts"
                loading={$postsStatus$ === STATUS.LOADING}
                on:loadMore
            />
        {/if}

        <InstagramPostModal
            postModalToken={instagramPostModalToken}
            instagramPostModalManager={postModalManager}
            showReviewAndQuestions={isCurrentAppGrowave &&
                gallery.showReviewsAndQuestionsOnProductView}
            highQualityEnabled={settings.highQualityEnabled}
            productQuickViewEnabled={settings.productQuickViewEnabled}
            hideBranding={gallery.hideBranding}
            popupTheme={gallery.popupTheme}
            {taggedProductQuery}
            {productsQuery}
        />
    {/if}
</ModalManagerProvider>

<style lang="postcss" global>
    .gw-instagram-gallery-brand {
        text-align: right;
        padding: var(--gw-space-5) 0;
    }
</style>
